import { Link, type Href } from 'expo-router';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type PressableProps,
  type ViewStyle,
} from 'react-native';
import { useState, type ReactNode } from 'react';

import { useTheme } from '@/theme';

import { Text } from './Text';

type Variant = 'primary' | 'secondary' | 'ghost' | 'ember';
type Size = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<PressableProps, 'children'> & {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  href?: Href;
  fullWidth?: boolean;
  trailingArrow?: boolean;
};

const sizes: Record<Size, { paddingVertical: number; paddingHorizontal: number; fontSize: number }> = {
  sm: { paddingVertical: 8, paddingHorizontal: 16, fontSize: 13 },
  md: { paddingVertical: 12, paddingHorizontal: 24, fontSize: 15 },
  lg: { paddingVertical: 18, paddingHorizontal: 34, fontSize: 17 },
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  href,
  fullWidth,
  trailingArrow,
  style,
  ...rest
}: ButtonProps) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const sz = sizes[size];

  const bgByVariant: Record<Variant, string> = {
    primary: theme.colors.accent,
    secondary: theme.colors.fg,
    ghost: 'transparent',
    ember: theme.colors.accentAlt,
  };
  const fgByVariant: Record<Variant, string> = {
    primary: theme.colors.invert,
    secondary: theme.colors.bg,
    ghost: theme.colors.fg,
    ember: theme.colors.invert,
  };
  const borderByVariant: Record<Variant, string> = {
    primary: 'transparent',
    secondary: 'transparent',
    ghost: theme.colors.fg,
    ember: 'transparent',
  };
  const hoverBg: Record<Variant, string> = {
    primary: theme.colors.accentDeep,
    secondary: theme.colors.fgSoft,
    ghost: theme.colors.fg,
    ember: theme.colors.accent,
  };
  const hoverFg: Record<Variant, string> = {
    primary: theme.colors.invert,
    secondary: theme.colors.bg,
    ghost: theme.colors.bg,
    ember: theme.colors.invert,
  };

  const baseStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: sz.paddingVertical,
    paddingHorizontal: sz.paddingHorizontal,
    borderRadius: theme.radii.pill,
    backgroundColor: hovered ? hoverBg[variant] : bgByVariant[variant],
    borderWidth: variant === 'ghost' ? 1.5 : 0,
    borderColor: borderByVariant[variant],
    alignSelf: fullWidth ? 'stretch' : 'flex-start',
    transform: hovered ? [{ translateY: -1 }] : undefined,
  };

  const inner = (
    <View style={baseStyle}>
      <Text
        style={{
          fontFamily: theme.fontFamily.bodyMedium,
          fontSize: sz.fontSize,
          color: hovered ? hoverFg[variant] : fgByVariant[variant],
          fontWeight: '500',
        }}
      >
        {children}
      </Text>
      {trailingArrow ? (
        <Text
          style={{
            color: hovered ? hoverFg[variant] : fgByVariant[variant],
            fontSize: sz.fontSize,
            transform: [{ translateX: hovered ? 3 : 0 }],
          }}
        >
          →
        </Text>
      ) : null}
    </View>
  );

  const hoverHandlers =
    Platform.OS === 'web'
      ? {
          onHoverIn: () => setHovered(true),
          onHoverOut: () => setHovered(false),
        }
      : {};

  const wrapStyle: ViewStyle = StyleSheet.flatten([
    styles.wrap,
    fullWidth && styles.full,
    style as ViewStyle,
  ]) as ViewStyle;

  if (href) {
    return (
      <Link href={href} asChild>
        <Pressable {...hoverHandlers} style={wrapStyle} {...rest}>
          {inner}
        </Pressable>
      </Link>
    );
  }

  return (
    <Pressable {...hoverHandlers} style={wrapStyle} {...rest}>
      {inner}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: { alignSelf: 'flex-start' },
  full: { alignSelf: 'stretch' },
});
